import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ControlledField } from '../ControlledField';
import { changeNoteSchema } from '@shared/schemas';
import { FormDialog } from './FormDialog';

type TChangeNoteFormValues = { note: string };

const DEFAULT_CHANGE_NOTE_FORM: TChangeNoteFormValues = { note: '' };
const changeNoteFormSchema: yup.ObjectSchema<TChangeNoteFormValues> = yup.object({ note: changeNoteSchema });

type TProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  title: string;
  onComplete: (note: string) => Promise<unknown>;
  defaultNote?: string;
};

const ChangeNoteDialog = ({ isOpen, setIsOpen, title, onComplete, defaultNote = '' }: TProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TChangeNoteFormValues>({
    resolver: yupResolver(changeNoteFormSchema),
    mode: 'onBlur',
    defaultValues: DEFAULT_CHANGE_NOTE_FORM,
  });

  useEffect(() => {
    if (isOpen) reset({ note: defaultNote });
  }, [isOpen]);

  const onSubmit = async ({ note }: TChangeNoteFormValues) => {
    try {
      await onComplete(note);
      setIsOpen(false);
    } catch {
      /* The caller's mutation reports the failure. Preserve the form for retry. */
    }
  };

  return (
    <FormDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={title}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel="Complete version"
      isSubmitting={isSubmitting}
    >
      <ControlledField
        label="Change note"
        name="note"
        control={control}
        errors={errors}
        required
        inputType="textarea"
        placeholder="Briefly describe what changed."
        className="h-28"
      />
    </FormDialog>
  );
};

export { ChangeNoteDialog };
